import * as React from "react";
import { ChevronDown, LogOut, Moon } from "lucide-react";
import { useAppDrawerContext } from "../../contexts/drawer.contexts";
import { useMatch, useNavigate, useResolvedPath } from "react-router-dom";
import { IthemeProviderProps } from "../../utils";
import { useAppThemeContext } from "../../contexts/theme.contexts";
import { useAppLoginContext } from "../../contexts/auth.contexts";
import { ENVIRONMENT } from "../../../environment/environment";
import { Avatar } from "../ui";

interface IListItemLink {
  to: string;
  icon: string;
  label: string;
  onClick: (() => void) | undefined;
}

const ListItemLink = ({ to, icon, label, onClick }: IListItemLink) => {
  const navigate = useNavigate();
  const resolvedPath = useResolvedPath(to);
  const match = useMatch({ path: resolvedPath.pathname, end: false });

  const handleClick = () => {
    navigate(to);
    onClick?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`mx-2 mb-1 flex min-h-[44px] w-[calc(100%-1rem)] items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors ${
        match
          ? "border border-primary-200 bg-primary-50 font-semibold text-primary-700 shadow-sm dark:border-transparent dark:bg-primary-600 dark:text-white"
          : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/60"
      }`}
    >
      <span className="material-icons shrink-0 text-[1.2rem]">{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
};

export function MenuLateral({ children }: IthemeProviderProps) {
  const { isDrawerOpen, toggleDrawerOpen, drawerOption } =
    useAppDrawerContext();
  const { toggleTheme } = useAppThemeContext();
  const { logout } = useAppLoginContext();

  const [openMenus, setOpenMenus] = React.useState<{ [key: string]: boolean }>(
    {}
  );

  const handleToggle = (path: string) => {
    setOpenMenus((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const avatar =
    ENVIRONMENT.BASE_URL_API + "/" + localStorage.getItem("avatar");
  const fullName = localStorage.getItem("fullName") || "Usuário";

  const sidebarContent = (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header com Avatar e Nome */}
      <div className="flex h-[148px] shrink-0 flex-col items-center justify-center gap-2 border-b border-slate-200 bg-white px-6 py-4 dark:border-slate-700 dark:bg-slate-800">
        <Avatar
          src={avatar}
          alt="Logo GWR"
          fallback={fullName}
          size={88}
          className="border-2 border-primary-500"
        />
        <span className="w-full truncate text-center text-sm font-semibold text-slate-800 dark:text-slate-100">
          {fullName}
        </span>
      </div>

      {/* Menu Principal */}
      <nav className="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden py-2">
        {drawerOption.map((option) =>
          option.subMenu && option.subMenu.length > 0 ? (
            <div key={option.path}>
              <button
                type="button"
                onClick={() => handleToggle(option.path)}
                className="mx-2 mb-1 flex min-h-[44px] w-[calc(100%-1rem)] items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/60"
              >
                <span className="material-icons shrink-0 text-[1.2rem]">
                  {option.icon}
                </span>
                <span className="flex-1 truncate">{option.label}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 transition-transform ${
                    openMenus[option.path] ? "rotate-180" : ""
                  }`}
                />
              </button>

              <div
                className={`overflow-hidden pl-2 transition-[max-height] duration-300 ease-in-out ${
                  openMenus[option.path] ? "max-h-[1000px]" : "max-h-0"
                }`}
              >
                {option.subMenu.map((child) => (
                  <ListItemLink
                    key={child.path}
                    to={child.path}
                    icon={child.icon}
                    label={child.label}
                    onClick={toggleDrawerOpen}
                  />
                ))}
              </div>
            </div>
          ) : (
            <ListItemLink
              key={option.path}
              to={option.path}
              icon={option.icon}
              label={option.label}
              onClick={toggleDrawerOpen}
            />
          )
        )}
      </nav>

      {/* Footer com ações */}
      <div className="flex shrink-0 items-center justify-center gap-1 border-t border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800">
        <button
          type="button"
          onClick={toggleTheme}
          className="flex min-h-[44px] flex-1 max-w-[160px] items-center gap-3 rounded-lg px-3 text-sm text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/60"
        >
          <Moon className="h-[1.2rem] w-[1.2rem] shrink-0" />
          <span>Alternar tema</span>
        </button>

        <button
          type="button"
          onClick={logout}
          className="flex min-h-[44px] flex-1 max-w-[120px] items-center gap-3 rounded-lg px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/30"
        >
          <LogOut className="h-[1.2rem] w-[1.2rem] shrink-0" />
          <span>Sair</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Overlay mobile */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={toggleDrawerOpen}
        />
      )}

      {/* Sidebar mobile (temporário/deslizante) */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-[260px] transform bg-white shadow-2xl transition-transform duration-300 ease-in-out dark:bg-slate-800 lg:hidden ${
          isDrawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Sidebar desktop (permanente) */}
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-[280px] border-r border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 lg:block xl:w-[300px]">
        {sidebarContent}
      </aside>

      <div className="h-screen lg:ml-[280px] xl:ml-[300px]">{children}</div>
    </>
  );
}
